import {
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
} from '../../../app/services/session.service';

const WORKSPACE_USERS_API_PATH = '/api/workspace/users';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function buildAuthFailureResult(response, fallbackMessage) {
  const payload = await parseJson(response);
  if (isSessionRefreshRequiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USERS_SESSION_REFRESH_REQUIRED',
      message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
    });
  }

  if (isSessionExpiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USERS_SESSION_EXPIRED',
      message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
    });
  }

  return buildResult({
    ok: false,
    error: 'WORKSPACE_USERS_AUTH_REQUIRED',
    message: fallbackMessage,
  });
}

function getWorkspaceUsersApiUrl(path = WORKSPACE_USERS_API_PATH) {
  return getAuthApiUrl(path);
}

function getWorkspaceUsersAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function mapApiWorkspaceUser(user) {
  const role = toCanonicalWorkspaceRole(user?.role);
  return {
    id: user?.id ?? null,
    fullName: user?.fullName || '',
    email: user?.email || '',
    role,
    permissionRole: role,
    teamType: toCanonicalTeamType(user?.teamType),
    companyRole: user?.companyRole || '',
    active: user?.active !== false,
    mustChangePassword: user?.mustChangePassword === true,
    tenantId: user?.tenantId || '',
    inviteStatus: user?.inviteStatus || '',
    inviteExpiresAt: user?.inviteExpiresAt || '',
    inviteToken: user?.inviteToken || '',
  };
}

function toCanonicalTeamType(teamType) {
  const normalized = (teamType || '').trim().replace(/[- ]+/g, '_').toUpperCase();
  return normalized === 'EXTERNAL' ? 'EXTERNAL' : 'INTERNAL';
}

function toCanonicalWorkspaceRole(role) {
  const normalized = (role || '').trim().replace(/[- ]+/g, '_').toUpperCase();
  if (normalized === 'TENANT_ADMIN') {
    return 'OWNER';
  }
  if (normalized === 'ADMIN' || normalized === 'ACCOUNT_MANAGER') {
    return 'MANAGER';
  }
  return normalized;
}

export async function listWorkspaceUsers() {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(), {
      headers: getWorkspaceUsersAuthHeaders(),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per la gestione utenti del workspace.');
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USERS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('WORKSPACE_USERS_INVALID_PAYLOAD');
    }

    return buildResult({
      ok: true,
      data: {
        users: payload.map(mapApiWorkspaceUser),
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USERS_UNAVAILABLE',
      message: 'Lista utenti workspace non disponibile.',
    });
  }
}

export async function createWorkspaceUser(payload) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(), {
      method: 'POST',
      headers: getWorkspaceUsersAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per creare utenti workspace.');
    }

    if (response.status === 409) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_EMAIL_CONFLICT',
        message: 'Email gia in uso.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_VALIDATION_ERROR',
        message: 'Dati utente non validi.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_CREATE_FAILED_${response.status}`);
    }

    const createdUser = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiWorkspaceUser(createdUser),
        inviteToken: createdUser?.inviteToken || '',
      },
      message: createdUser?.inviteToken ? 'Utente workspace creato. Copia il link invito.' : 'Utente workspace creato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_CREATE_UNAVAILABLE',
      message: 'Creazione utente workspace non completata.',
    });
  }
}

export async function deleteWorkspaceUser(userId) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}`), {
      method: 'DELETE',
      headers: getWorkspaceUsersAuthHeaders(),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per eliminare utenti workspace.');
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_DELETE_NOT_ALLOWED',
        message: 'Eliminazione utente non consentita.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_DELETE_FAILED_${response.status}`);
    }

    return buildResult({
      ok: true,
      message: 'Utente workspace eliminato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_DELETE_UNAVAILABLE',
      message: 'Eliminazione utente non completata.',
    });
  }
}

export async function updateWorkspaceUserStatus(userId, active) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}/status`), {
      method: 'PATCH',
      headers: getWorkspaceUsersAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ active }),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per aggiornare lo stato utente.');
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_STATUS_INVALID',
        message: 'Aggiornamento stato utente non consentito.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_STATUS_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiWorkspaceUser(payload),
      },
      message: active ? 'Account utente attivato.' : 'Account utente disattivato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_STATUS_UNAVAILABLE',
      message: 'Aggiornamento stato account non completato.',
    });
  }
}

export async function updateWorkspaceUserIdentity(userId, payload) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}`), {
      method: 'PATCH',
      headers: getWorkspaceUsersAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per modificare utente.');
    }

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_IDENTITY_FORBIDDEN',
        message: 'Modifica utente non consentita.',
      });
    }

    if (response.status === 409) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_EMAIL_CONFLICT',
        message: 'Email gia in uso.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_IDENTITY_VALIDATION_ERROR',
        message: 'Nome o email non validi.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_IDENTITY_FAILED_${response.status}`);
    }

    const updatedUser = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiWorkspaceUser(updatedUser),
      },
      message: 'Utente workspace aggiornato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_IDENTITY_UNAVAILABLE',
      message: 'Modifica utente non completata.',
    });
  }
}

export async function updateWorkspaceUserRole(userId, role) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}/role`), {
      method: 'PATCH',
      headers: getWorkspaceUsersAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ role }),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per aggiornare il ruolo utente.');
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_ROLE_NOT_ALLOWED',
        message: 'Aggiornamento ruolo non consentito.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_ROLE_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiWorkspaceUser(payload),
      },
      message: 'Ruolo utente aggiornato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_ROLE_UNAVAILABLE',
      message: 'Aggiornamento ruolo non completato.',
    });
  }
}

export async function resetWorkspaceUserPassword(userId, password) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}/password`), {
      method: 'PATCH',
      headers: getWorkspaceUsersAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ password }),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per reimpostare la password.');
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_PASSWORD_NOT_ALLOWED',
        message: 'Reimpostazione password non consentita.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_PASSWORD_FAILED_${response.status}`);
    }

    return buildResult({
      ok: true,
      message: 'Password utente reimpostata.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_PASSWORD_UNAVAILABLE',
      message: 'Reimpostazione password non completata.',
    });
  }
}

export async function resendWorkspaceUserInvite(userId) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}/invite/resend`), {
      method: 'POST',
      headers: getWorkspaceUsersAuthHeaders(),
    });

    if (response.status === 401 || response.status === 403) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per reinviare invito.');
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_INVITE_NOT_ALLOWED',
        message: 'Reinvio invito non consentito.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_USER_INVITE_RESEND_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildResult({
      ok: true,
      data: {
        inviteToken: payload?.inviteToken || '',
        inviteStatus: payload?.status || '',
        inviteExpiresAt: payload?.expiresAt || '',
      },
      message: 'Invito utente reinviato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_USER_INVITE_RESEND_UNAVAILABLE',
      message: 'Reinvio invito non completato.',
    });
  }
}

export async function transferWorkspaceOwnership(userId) {
  try {
    const response = await fetch(getWorkspaceUsersApiUrl(`${WORKSPACE_USERS_API_PATH}/${userId}/transfer-ownership`), {
      method: 'POST',
      headers: getWorkspaceUsersAuthHeaders(),
    });

    if (response.status === 401) {
      return await buildAuthFailureResult(response, 'Sessione non autorizzata per trasferire ownership.');
    }

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_OWNERSHIP_FORBIDDEN',
        message: 'Solo owner corrente puo trasferire ownership.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_OWNERSHIP_TARGET_INVALID',
        message: 'Trasferimento ownership non consentito per questo utente.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_USER_NOT_FOUND',
        message: 'Utente workspace non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_OWNERSHIP_TRANSFER_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('WORKSPACE_OWNERSHIP_INVALID_PAYLOAD');
    }

    return buildResult({
      ok: true,
      data: {
        users: payload.map(mapApiWorkspaceUser),
      },
      message: 'Ownership trasferita.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_OWNERSHIP_UNAVAILABLE',
      message: 'Trasferimento ownership non completato.',
    });
  }
}
